export const XP_PER_LEVEL = 100;
export const ARCADE_ACCENT = "#ec4899";

export const QUICK_PLAY_CONFIG = {
  category: "career_stats",
  difficulty: "medium",
  questionCount: 5,
  timeLimit: 30,
  gameMode: "quick",
  sport: "mlb",
};

const STARTING_COPY = {
  daily: {
    title: "Setting up the Daily Challenge",
    subtitle: "Same 10 questions for everyone — lining them up now.",
  },
  quick: {
    title: "Dealing your questions",
    subtitle: "Building 5 matchups from career stats. This can take a few seconds.",
  },
  custom: {
    title: "Dealing your questions",
    subtitle: "Building your custom matchups. This can take a few seconds.",
  },
};

const DEFAULT_STARTING_COPY = {
  title: "Dealing your questions",
  subtitle: "This can take a few seconds.",
};

export function getTriviaStartingCopy(gameMode) {
  return STARTING_COPY[gameMode] || DEFAULT_STARTING_COPY;
}

export const DAILY_PLAY_CONFIG = {
  category: "career_stats",
  difficulty: "medium",
  questionCount: 10,
  timeLimit: 30,
  gameMode: "daily",
  sport: "mlb",
};

const CATEGORY_LABELS = {
  career_hitting: "Career Hitting",
  career_pitching: "Career Pitching",
  career_fielding: "Career Fielding",
  career_scoring: "Career Scoring",
  career_rebounding: "Career Rebounding",
  career_playmaking: "Career Playmaking",
  career_defense: "Career Defense",
  career_passing: "Career Passing",
  career_rushing: "Career Rushing",
  career_receiving: "Career Receiving",
  career_stats: "Career Stats",
  team_history: "Team History",
  all_time_greats: "All-Time Greats",
  all: "All Categories",
};

const SUBCATEGORY_LABELS = {
  points: "Points",
  ppg: "Points Per Game",
  rebounds: "Rebounds",
  steals: "Steals",
  blocks: "Blocks",
  passing_yards: "Passing Yards",
  passing_tds: "Passing TDs",
  rushing_yards: "Rushing Yards",
  rushing_tds: "Rushing TDs",
  receiving_yards: "Receiving Yards",
  receiving_tds: "Receiving TDs",
  receptions: "Receptions",
  home_runs: "Home Runs",
  batting_average: "Batting Average",
  rbi: "RBI",
  hits: "Hits",
  doubles: "Doubles",
  triples: "Triples",
  stolen_bases: "Stolen Bases",
  walks: "Walks",
  wins: "Wins",
  strikeouts: "Strikeouts",
  era: "ERA",
  saves: "Saves",
  shutouts: "Shutouts",
  complete_games: "Complete Games",
  fielding_percentage: "Fielding Percentage",
  range_factor: "Range Factor",
  putouts: "Putouts",
  assists: "Assists",
  errors: "Errors",
};

const FORMAT_LABELS = {
  higher_lower: "Higher / lower",
  multiple_choice: "Multiple choice",
  true_false: "True / false",
};

export function getLetterGrade(accuracy) {
  if (accuracy >= 0.9) return "S";
  if (accuracy >= 0.8) return "A";
  if (accuracy >= 0.7) return "B";
  if (accuracy >= 0.6) return "C";
  return "D";
}

export function getXpProgress(totalXp) {
  const safeXp = Math.max(0, totalXp || 0);
  const level = Math.floor(safeXp / XP_PER_LEVEL) + 1;
  const xpIntoLevel = safeXp % XP_PER_LEVEL;
  return {
    level,
    xpIntoLevel,
    xpPerLevel: XP_PER_LEVEL,
    percent: (xpIntoLevel / XP_PER_LEVEL) * 100,
  };
}

export function getComboLabel(streak) {
  return streak >= 3 ? "Combo x1.5" : null;
}

export function withSport(config, sport = "mlb") {
  return { ...config, sport };
}

const SPORT_CUSTOM_CATEGORY_IDS = {
  nba: ["career_scoring", "career_rebounding", "career_playmaking", "career_defense", "career_stats"],
  nfl: ["career_passing", "career_rushing", "career_receiving", "career_stats"],
  mlb: ["career_hitting", "career_pitching", "career_fielding", "career_stats"],
};

// Fix 5: per-sport id list resolved through CATEGORY_LABELS — single source of truth (DRY).
export function customCategoriesForSport(sport = "mlb") {
  const ids = SPORT_CUSTOM_CATEGORY_IDS[sport] || SPORT_CUSTOM_CATEGORY_IDS.mlb;
  return ids.map((id) => ({ value: id, label: getCategoryLabel(id) }));
}

// Fix 3: clamp a category to a valid value for the given sport (prevents cross-sport 400s).
export function resolveCustomCategory(sport, category) {
  const valid = customCategoriesForSport(sport).map((c) => c.value);
  return valid.includes(category) ? category : "career_stats";
}

export function buildShareText({ mode, correct, total }) {
  if (mode === "daily") {
    return `I scored ${correct}/${total} on today's stats.fan Daily Challenge 🔥`;
  }
  return `I scored ${correct}/${total} on stats.fan trivia 🔥`;
}

function optionLabel(option) {
  return String(option?.text ?? option?.displayName ?? "").trim();
}

export function isTrueFalseOption(option) {
  if (!option || option.playerId != null) return false;
  if (typeof option.value === "boolean") return true;
  const label = optionLabel(option).toLowerCase();
  return label === "true" || label === "false";
}

export function trueFalseCardPresentation(option) {
  const isTrue =
    option?.value === true || optionLabel(option).toLowerCase() === "true";
  return {
    label: isTrue ? "TRUE" : "FALSE",
    polarity: isTrue ? "positive" : "negative",
  };
}

export function detectQuestionType(question) {
  if (question?.type) return question.type;
  if (isTrueFalseOption(question?.options?.[0])) return "true_false";
  if (question?.options?.[0]?.text !== undefined) return "true_false";
  return "multiple_choice";
}

export function optionAnswerValue(option) {
  if (option?.playerId !== undefined) return option.playerId;
  return option?.value;
}

export function optionMatchesCorrectAnswer(option, correct) {
  if (correct == null || option == null) return false;
  const value = optionAnswerValue(option);

  if (typeof correct === "boolean") {
    return value === correct;
  }

  if (typeof correct === "object") {
    if (correct.playerId && option.playerId && correct.playerId === option.playerId) {
      return true;
    }
    if (correct.value !== undefined && value === correct.value) {
      return true;
    }
    const correctName = optionLabel(correct);
    const optionName = optionLabel(option);
    if (correctName && optionName && correctName === optionName) {
      return true;
    }
  }

  return value === correct;
}

export function getCategoryLabel(category) {
  return CATEGORY_LABELS[category] || category;
}

export function getTriviaFormatLabel(format) {
  return FORMAT_LABELS[format] || format;
}

export function getSubcategoryLabel(subcategory) {
  if (!subcategory) return "";
  return (
    SUBCATEGORY_LABELS[subcategory] ||
    subcategory.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function formatStatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (Number.isInteger(n)) return n.toLocaleString("en-US");
  return String(n);
}

export function formatStatValue(subcategory, value) {
  if (value === null || value === undefined) return "";
  const formatted = formatStatNumber(value);
  const label = getSubcategoryLabel(subcategory);
  return label ? `${formatted} ${label}` : formatted;
}

export function withRevealedStatValues(options = [], sourceData) {
  const players = sourceData?.players;
  if (!players?.length) return options;
  const byId = new Map(players.map((player) => [player.playerId, player.statValue]));
  return options.map((option) => {
    if (!option?.playerId || !byId.has(option.playerId)) return option;
    return { ...option, statValue: byId.get(option.playerId) };
  });
}
